import type { RunResultResponse } from '@blockbattle/shared';
import { gameCheckpointStorage } from './game-top-up.js';

export interface PendingResult {
  kind?: 'PATH';
  id: string;
  token: string;
  moves: readonly unknown[];
  actions?: readonly unknown[];
  forfeit?: boolean;
  requestKey: string;
  at: number;
  error?: string;
  retryable?: boolean;
  archivedAt?: number;
}
const key = (userId: string) => `bb:result-outbox:${userId}`;
export const resultHistory = (userId: string): PendingResult[] => {
  try {
    const value: unknown = JSON.parse(gameCheckpointStorage.getItem(key(userId)) ?? '[]');
    if (!Array.isArray(value)) return [];
    return value.filter((entry: unknown): entry is PendingResult =>
      Boolean(
        entry &&
        typeof entry === 'object' &&
        'id' in entry &&
        typeof entry.id === 'string' &&
        'token' in entry &&
        typeof entry.token === 'string' &&
        'requestKey' in entry &&
        typeof entry.requestKey === 'string' &&
        'at' in entry &&
        typeof entry.at === 'number' &&
        'moves' in entry &&
        Array.isArray(entry.moves) &&
        (!('actions' in entry) || entry.actions === undefined || Array.isArray(entry.actions)),
      ),
    );
  } catch {
    return [];
  }
};
export const pendingResults = (userId: string): PendingResult[] =>
  resultHistory(userId).filter((entry) => !entry.archivedAt);
export const archiveResult = (owner: string, id: string): boolean => {
  const entry = resultHistory(owner).find((value) => value.id === id);
  if (!entry || entry.retryable !== false || !hasDurableResult(owner, id)) return false;
  if (!queueResult(owner, { ...entry, archivedAt: Date.now() })) return false;
  if (!hasDurableResult(owner, id)) {
    queueResult(owner, entry);
    return false;
  }
  detachResultJournal(entry);
  return true;
};
export const exportResultJournal = (entry: PendingResult): string =>
  JSON.stringify(
    {
      runId: entry.id,
      kind: entry.kind ?? 'RANKED',
      at: entry.at,
      moves: entry.moves,
      actions: entry.actions,
      forfeit: entry.forfeit,
      error: entry.error,
      archivedAt: entry.archivedAt,
    },
    null,
    2,
  );
export const queueResult = (owner: string, entry: PendingResult): boolean => {
  const entries = resultHistory(owner).filter((value) => value.id !== entry.id);
  gameCheckpointStorage.setItem(key(owner), JSON.stringify([...entries, entry]));
  return resultHistory(owner).some((value) => JSON.stringify(value) === JSON.stringify(entry));
};
export const clearRunJournal = (requestKey: string, id: string): void => {
  // Another tab may have already written a newer run into persistent storage.
  for (const getStorage of [() => localStorage, () => sessionStorage]) {
    try {
      const storage = getStorage();
      if (storage.getItem(`${requestKey}_id`) !== id) continue;
      for (const suffix of ['', '_id', '_moves']) storage.removeItem(requestKey + suffix);
    } catch {
      /* Preserve whichever copy is still available. */
    }
  }
};
export const detachResultJournal = (
  entry: Pick<PendingResult, 'kind' | 'requestKey' | 'id'>,
): void => {
  if (entry.kind !== 'PATH') {
    clearRunJournal(entry.requestKey, entry.id);
    return;
  }
  for (const getStorage of [() => localStorage, () => sessionStorage]) {
    try {
      const storage = getStorage();
      const checkpoint = JSON.parse(storage.getItem(entry.requestKey) ?? 'null') as {
        runId?: string;
      } | null;
      if (checkpoint?.runId !== entry.id) continue;
      storage.removeItem(entry.requestKey);
      storage.removeItem(`${entry.requestKey}:request`);
    } catch {
      /* Keep unreadable evidence. */
    }
  }
};
export const acknowledgeResult = (owner: string, id: string): void => {
  const entries = resultHistory(owner);
  const entry = entries.find((value) => value.id === id);
  if (entry) detachResultJournal(entry);
  gameCheckpointStorage.setItem(
    key(owner),
    JSON.stringify(entries.filter((value) => value.id !== id)),
  );
};
export const canRetryResult = (error: unknown): boolean => {
  const status = error && typeof error === 'object' && 'status' in error ? error.status : 0;
  return (
    status === undefined ||
    status === 0 ||
    status === 401 ||
    status === 408 ||
    status === 429 ||
    (typeof status === 'number' && status >= 500)
  );
};
const deliveries = new Map<string, Promise<RunResultResponse>>();
/** Same-tab retries share one request. Server-side idempotency handles multiple tabs/devices. */
export const deliverResultOnce = (id: string, send: () => Promise<RunResultResponse>) => {
  const existing = deliveries.get(id);
  if (existing) return existing;
  const pending = send().finally(() => deliveries.delete(id));
  deliveries.set(id, pending);
  return pending;
};

/** A background exit requires persistent storage, not only this tab's fallback copy. */
export const hasDurableResult = (owner: string, id: string): boolean => {
  try {
    const queued = resultHistory(owner).find((entry) => entry.id === id);
    const stored: unknown = JSON.parse(localStorage.getItem(key(owner)) ?? '[]');
    return Boolean(
      queued &&
      Array.isArray(stored) &&
      stored.some((entry) => JSON.stringify(entry) === JSON.stringify(queued)),
    );
  } catch {
    return false;
  }
};

export async function boundedResultWait<T>(operation: Promise<T>, timeoutMs = 8000): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('Результат ожидает отправки. Повторим при подключении.')),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
